/**
 * Types + i18n label helpers for the change_request workflow family (#739/#737).
 * Mirrors the backend `models.ChangeRequestPayload` / the `children` family
 * `ChildDataUpdateSpec` (decoded from `payload.executor.body`) and the generated
 * producer-agnostic create DTOs (`change_requests_CreateChangeRequestsRequest`).
 */

import type { change_requests_CreateChangeRequestsRequest } from '@/api/models/change_requests_CreateChangeRequestsRequest';
import type { change_requests_ChangeRequestResultDTO } from '@/api/models/change_requests_ChangeRequestResultDTO';
import type { workflows_ChangeRequestSubjectDTO } from '@/api/models/workflows_ChangeRequestSubjectDTO';
import type { workflows_ChangeRequestSubjectChildDTO } from '@/api/models/workflows_ChangeRequestSubjectChildDTO';
import { getEmployeeDataSectionLabel } from './employeeDataChangeRequest';

// Every change_request family the backend declares -- mirrors the
// ChangeRequestFamily* constants in internal/models/workflow.go.
export const CHANGE_REQUEST_FAMILIES = [
  'children',
  'care_day',
  'pause',
  'group_allocation',
  'contact_data',
  'contract',
  'employee_data',
  'time_correction',
] as const;
export type ChangeRequestFamily = (typeof CHANGE_REQUEST_FAMILIES)[number];

export const CHANGE_REQUEST_SUB_TYPES = [
  'comment',
  'allergy',
  'medical',
  'contact_pickup',
  'contact_emergency',
] as const;

export type ChangeRequestSubType = (typeof CHANGE_REQUEST_SUB_TYPES)[number];

// Sub-types of the care_day family (#783) — mirrors
// internal/models/workflow.go (ChangeRequestSubTypeAddDay / RemoveDay).
export const CARE_DAY_SUB_TYPES = ['add_day', 'remove_day'] as const;
export type CareDaySubType = (typeof CARE_DAY_SUB_TYPES)[number];

// Sub-types of the pause family (#784) — mirrors
// internal/models/workflow.go (ChangeRequestSubTypePauseCreate / Extend / Cancel).
export const PAUSE_SUB_TYPES = ['create', 'extend', 'cancel'] as const;
export type PauseSubType = (typeof PAUSE_SUB_TYPES)[number];

// Sensitivity mirrors the backend `sensitiveChangeRequestSubTypes` set — sensitive
// sub_types always require approval and can never auto-apply.
const SENSITIVE_SUB_TYPES: ReadonlySet<string> = new Set([
  'allergy',
  'medical',
  'contact_pickup',
  'contact_emergency',
  'basic',
  // contact_data privacy requests (#1876).
  'data_access_request',
  'account_deletion_request',
]);

export function isSensitiveSubType(subType: string): boolean {
  return SENSITIVE_SUB_TYPES.has(subType);
}

export type I18nLike = { _: (descriptor: { id: string; message: string }) => string };

// Privacy requests a parent files under `contact_data` (#1876) -- mirrors the
// backend ChangeRequestSubTypeDataAccessRequest / AccountDeletionRequest.
export const CONTACT_PRIVACY_SUB_TYPES = ['data_access_request', 'account_deletion_request'] as const;
export type ContactPrivacySubType = (typeof CONTACT_PRIVACY_SUB_TYPES)[number];

/** True for a `contact_data` privacy sub-type (data access or account deletion). */
export function isContactPrivacySubType(subType: string | null | undefined): subType is ContactPrivacySubType {
  return subType === 'data_access_request' || subType === 'account_deletion_request';
}

/** Label of a privacy request ("Data access request", "Account deletion"); "" for any other sub-type. */
export function getContactPrivacySubTypeLabel(i18n: I18nLike, subType: string | null | undefined): string {
  switch (subType) {
    case 'data_access_request':
      return i18n._({ id: 'changeRequest.subType.data_access_request', message: 'Data access request' });
    case 'account_deletion_request':
      return i18n._({ id: 'changeRequest.subType.account_deletion_request', message: 'Account deletion' });
    default:
      return '';
  }
}

export function getSubTypeLabel(i18n: I18nLike, subType: string): string {
  switch (subType) {
    case 'comment':
      return i18n._({ id: 'changeRequest.subType.comment', message: 'Comment' });
    case 'allergy':
      return i18n._({ id: 'changeRequest.subType.allergy', message: 'Allergy' });
    case 'medical':
      return i18n._({ id: 'changeRequest.subType.medical', message: 'Medical' });
    case 'contact_pickup':
      return i18n._({ id: 'changeRequest.subType.contact_pickup', message: 'Pick-up authorization' });
    case 'contact_emergency':
      return i18n._({ id: 'changeRequest.subType.contact_emergency', message: 'Emergency contact' });
    case 'basic':
      return i18n._({ id: 'changeRequest.subType.basic', message: 'Name, birthdate, gender or nationality' });
    case 'add_day':
      return i18n._({ id: 'changeRequest.subType.add_day', message: 'Add care day' });
    case 'remove_day':
      return i18n._({ id: 'changeRequest.subType.remove_day', message: 'Remove care day' });
    // Pause family sub_types (#784) — delegate to the canonical pause labels so the
    // create form's sub_type select and the read-side preview stay in sync.
    case 'create':
    case 'extend':
    case 'cancel':
      return getPauseSubTypeLabel(i18n, subType);
    // contact_data privacy requests (#1876).
    case 'data_access_request':
    case 'account_deletion_request':
      return getContactPrivacySubTypeLabel(i18n, subType);
    default:
      return subType;
  }
}

/**
 * Localized weekday name for an ISO weekday number (1 = Monday .. 7 = Sunday),
 * used by the care_day billing-delta preview. Returns the raw number as a string
 * for an out-of-range value (defensive).
 */
export function getWeekdayLabel(i18n: I18nLike, weekday: number): string {
  switch (weekday) {
    case 1:
      return i18n._({ id: 'weekday.monday', message: 'Monday' });
    case 2:
      return i18n._({ id: 'weekday.tuesday', message: 'Tuesday' });
    case 3:
      return i18n._({ id: 'weekday.wednesday', message: 'Wednesday' });
    case 4:
      return i18n._({ id: 'weekday.thursday', message: 'Thursday' });
    case 5:
      return i18n._({ id: 'weekday.friday', message: 'Friday' });
    case 6:
      return i18n._({ id: 'weekday.saturday', message: 'Saturday' });
    case 7:
      return i18n._({ id: 'weekday.sunday', message: 'Sunday' });
    default:
      return String(weekday);
  }
}

// Localized operation verb (add/update/remove) — reuses the canonical-form
// `changeRequest.op.*` catalog entries so the read-side summary is localized too.
export function getOperationLabel(i18n: I18nLike, op: string | undefined): string {
  switch (op) {
    case 'add':
      return i18n._({ id: 'changeRequest.op.add', message: 'Add' });
    case 'update':
      return i18n._({ id: 'changeRequest.op.update', message: 'Update' });
    case 'remove':
      return i18n._({ id: 'changeRequest.op.remove', message: 'Remove' });
    default:
      return op ?? '';
  }
}

// Localized medical record kind (disease/medication) — reuses the canonical-form
// `changeRequest.kind.*` catalog entries.
export function getMedicalKindLabel(i18n: I18nLike, kind: string | undefined): string {
  switch (kind) {
    case 'disease':
      return i18n._({ id: 'changeRequest.kind.disease', message: 'Disease' });
    case 'medication':
      return i18n._({ id: 'changeRequest.kind.medication', message: 'Medication' });
    default:
      return kind ?? '';
  }
}

/**
 * Localized label for a pause sub_type (#784): create → "Pause created",
 * extend → "Pause changed", cancel → "Pause cancelled". Used by the pause
 * billing-delta preview to describe what the change does.
 */
export function getPauseSubTypeLabel(i18n: I18nLike, subType: string | undefined): string {
  switch (subType) {
    case 'create':
      return i18n._({ id: 'pause.plan.subType.create', message: 'Pause created' });
    case 'extend':
      return i18n._({ id: 'pause.plan.subType.extend', message: 'Pause changed' });
    case 'cancel':
      return i18n._({ id: 'pause.plan.subType.cancel', message: 'Pause cancelled' });
    default:
      return subType ?? '';
  }
}

export function getFamilyLabel(i18n: I18nLike, family: string): string {
  switch (family) {
    case 'children':
      return i18n._({ id: 'changeRequest.family.children', message: 'Children' });
    case 'care_day':
      return i18n._({ id: 'changeRequest.family.care_day', message: 'Care day' });
    case 'pause':
      return i18n._({ id: 'changeRequest.family.pause', message: 'Pause' });
    case 'group_allocation':
      return i18n._({ id: 'changeRequest.family.group_allocation', message: 'Group allocation' });
    // Parent-app families (#1715): declared only, no executor, route to manual.
    case 'contact_data':
      return i18n._({ id: 'changeRequest.family.contact_data', message: 'Contact details' });
    case 'contract':
      return i18n._({ id: 'changeRequest.family.contract', message: 'Contract' });
    case 'child_contact':
      return i18n._({ id: 'changeRequest.family.child_contact', message: 'New contact' });
    // Staff self-service (#1766): an employee's own address, phone or emergency contacts.
    case 'employee_data':
      return i18n._({ id: 'changeRequest.family.employeeData', message: 'Personal data' });
    // Staff self-service (#1979): an employee's correction of one day's worked time.
    case 'time_correction':
      return i18n._({ id: 'changeRequest.family.timeCorrection', message: 'Time correction' });
    default:
      return family;
  }
}

// Contract request kinds a parent can send (#1715) -- mirrors the backend
// ParentContractKind* constants (internal/services/change_requests/parent_spec.go).
export const CONTRACT_REQUEST_KINDS = ['care_days', 'pause', 'start_end', 'terminate', 'other'] as const;
export type ContractRequestKind = (typeof CONTRACT_REQUEST_KINDS)[number];

/**
 * Localized label for a parent contract request kind (the `contract` family's
 * `executor.body.kind`, also stored as `sub_type`). Returns the raw kind for an
 * unknown value.
 */
export function getContractKindLabel(i18n: I18nLike, kind: string | undefined): string {
  switch (kind) {
    case 'care_days':
      return i18n._({ id: 'changeRequest.contractKind.care_days', message: 'Change care days' });
    case 'pause':
      return i18n._({ id: 'changeRequest.contractKind.pause', message: 'Pause the contract' });
    case 'start_end':
      return i18n._({ id: 'changeRequest.contractKind.start_end', message: 'Change start or end date' });
    case 'terminate':
      return i18n._({ id: 'changeRequest.contractKind.terminate', message: 'Terminate the contract' });
    case 'other':
      return i18n._({ id: 'changeRequest.contractKind.other', message: 'Other change' });
    default:
      return kind ?? '';
  }
}

/**
 * Short type label of a change request for inbox cards and list rows. A
 * `contract` item names its kind in `sub_type`; `contact_data` and
 * `group_allocation` carry no `sub_type`, so they are labelled by family (#1715),
 * except a `contact_data` privacy request (#1876), which is named by its kind.
 */
export function getChangeRequestTypeLabel(
  i18n: I18nLike,
  view: Pick<ChangeRequestPayloadView, 'family' | 'sub_type'> | null | undefined,
): string {
  const family = view?.family ?? '';
  const subType = view?.sub_type ?? '';
  if (family === 'contract') return getContractKindLabel(i18n, subType);
  // child_contact has one server-set sub_type ("add"); the family names it.
  if (family === 'child_contact') return getFamilyLabel(i18n, family);
  if (family === 'employee_data') return getEmployeeDataSectionLabel(i18n, subType);
  // time_correction's sub_type is the rule (shift/tracking); the family names it.
  if (family === 'time_correction') return getFamilyLabel(i18n, family);
  // A plain contact edit has no sub_type; a privacy request (#1876) names its kind
  // (getSubTypeLabel alone would fall back to the raw value for an unknown one).
  if (family === 'contact_data') {
    return isContactPrivacySubType(subType) ? getContactPrivacySubTypeLabel(i18n, subType) : getFamilyLabel(i18n, family);
  }
  return subType ? getSubTypeLabel(i18n, subType) : getFamilyLabel(i18n, family);
}

// Keys a parent may change in a `contact_data` request, in display order --
// mirrors the backend allowlist (contactFieldRules in contact_data_spec.go).
export const CONTACT_CHANGE_KEYS = [
  'title',
  'addressForm',
  'gender',
  'firstname',
  'lastname',
  'birthdate',
  'nationalities',
  'languageCorrespondence',
  'emailMain',
  'emailSecond',
  'telMobileCountryCode',
  'telMobile',
  'telHomeCountryCode',
  'telHome',
  'telWorkCountryCode',
  'telWork',
  'address',
  'employer',
  'profession',
] as const;

// Sub-keys of the `contact_data` `changes.address` object, in display order.
export const CONTACT_ADDRESS_KEYS = ['street', 'streetNumber', 'info2', 'zip', 'city', 'country'] as const;

/**
 * Localized label for a `contact_data` change key (one of the `changes` keys
 * or one of the address sub-keys). Returns the raw key for an unknown value.
 */
export function getContactFieldLabel(i18n: I18nLike, key: string): string {
  switch (key) {
    case 'title':
      return i18n._({ id: 'changeRequest.contactField.title', message: 'Title' });
    case 'addressForm':
      return i18n._({ id: 'changeRequest.contactField.addressForm', message: 'Form of address' });
    case 'gender':
      return i18n._({ id: 'changeRequest.contactField.gender', message: 'Gender' });
    case 'firstname':
      return i18n._({ id: 'changeRequest.contactField.firstname', message: 'First name' });
    case 'lastname':
      return i18n._({ id: 'changeRequest.contactField.lastname', message: 'Last name' });
    case 'birthdate':
      return i18n._({ id: 'changeRequest.contactField.birthdate', message: 'Date of birth' });
    case 'nationalities':
      return i18n._({ id: 'changeRequest.contactField.nationalities', message: 'Nationalities' });
    case 'emailMain':
      return i18n._({ id: 'changeRequest.contactField.emailMain', message: 'Email' });
    case 'emailSecond':
      return i18n._({ id: 'changeRequest.contactField.emailSecond', message: 'Second email' });
    case 'telMobile':
      return i18n._({ id: 'changeRequest.contactField.telMobile', message: 'Mobile phone' });
    case 'telMobileCountryCode':
      return i18n._({ id: 'changeRequest.contactField.telMobileCountryCode', message: 'Mobile phone country code' });
    case 'telHome':
      return i18n._({ id: 'changeRequest.contactField.telHome', message: 'Home phone' });
    case 'telHomeCountryCode':
      return i18n._({ id: 'changeRequest.contactField.telHomeCountryCode', message: 'Home phone country code' });
    case 'telWork':
      return i18n._({ id: 'changeRequest.contactField.telWork', message: 'Work phone' });
    case 'telWorkCountryCode':
      return i18n._({ id: 'changeRequest.contactField.telWorkCountryCode', message: 'Work phone country code' });
    case 'address':
      return i18n._({ id: 'changeRequest.contactField.address', message: 'Address' });
    case 'employer':
      return i18n._({ id: 'changeRequest.contactField.employer', message: 'Employer' });
    case 'profession':
      return i18n._({ id: 'changeRequest.contactField.profession', message: 'Profession' });
    case 'languageCorrespondence':
      return i18n._({ id: 'changeRequest.contactField.languageCorrespondence', message: 'Correspondence language' });
    case 'street':
      return i18n._({ id: 'changeRequest.contactField.street', message: 'Street' });
    case 'streetNumber':
      return i18n._({ id: 'changeRequest.contactField.streetNumber', message: 'Street number' });
    case 'info2':
      return i18n._({ id: 'changeRequest.contactField.info2', message: 'Address line 2' });
    case 'zip':
      return i18n._({ id: 'changeRequest.contactField.zip', message: 'Postal code' });
    case 'city':
      return i18n._({ id: 'changeRequest.contactField.city', message: 'City' });
    case 'country':
      return i18n._({ id: 'changeRequest.contactField.country', message: 'Country' });
    default:
      return key;
  }
}

/**
 * Localized display value of a coded `contact_data` value: the form of
 * address, the gender and the correspondence language. Any other key, and an
 * unknown code, is shown as sent.
 */
export function getContactValueLabel(i18n: I18nLike, key: string, value: string): string {
  switch (`${key}:${value}`) {
    case 'addressForm:FORMAL':
      return i18n._({ id: 'changeRequest.contactValue.addressForm.formal', message: 'Formal' });
    case 'addressForm:INFORMAL':
      return i18n._({ id: 'changeRequest.contactValue.addressForm.informal', message: 'Informal' });
    case 'gender:MALE':
      return i18n._({ id: 'changeRequest.contactValue.gender.male', message: 'Male' });
    case 'gender:FEMALE':
      return i18n._({ id: 'changeRequest.contactValue.gender.female', message: 'Female' });
    case 'gender:OTHER':
      return i18n._({ id: 'changeRequest.contactValue.gender.other', message: 'Other' });
    case 'gender:UNKNOWN':
      return i18n._({ id: 'changeRequest.contactValue.gender.unknown', message: 'Unknown' });
    case 'gender:UNDEFINED':
      return i18n._({ id: 'changeRequest.contactValue.gender.undefined', message: 'Not specified' });
    case 'languageCorrespondence:de':
      return i18n._({ id: 'changeRequest.contactValue.language.de', message: 'German' });
    case 'languageCorrespondence:en':
      return i18n._({ id: 'changeRequest.contactValue.language.en', message: 'English' });
    default:
      return value;
  }
}

// The employee_data family (#1766) lives in its own module.
export * from './employeeDataChangeRequest';
// The time_correction family (#1979) lives in its own module too.
export * from './timeCorrectionChangeRequest';

/**
 * Families an employee files about their own record (employee_data #1766,
 * time_correction #1979): never edited in the inbox, and a reject needs a
 * reason the employee reads.
 */
export function isEmployeeFiledFamily(family: string | undefined): boolean {
  return family === 'employee_data' || family === 'time_correction';
}

export const COMMENT_FIELDS = [
  'notes',
  'special_care',
  'note_emergency_list',
  'language_request',
  'sleep_request',
  'eating_request',
  'toilet_request',
  'misc_request',
  'vaccination_comment',
] as const;

export type CommentField = (typeof COMMENT_FIELDS)[number];

// Human-readable, localized labels for the child comment fields (the stored
// payload carries the raw snake_case field name).
export function getCommentFieldLabel(i18n: I18nLike, field: string): string {
  switch (field) {
    case 'notes':
      return i18n._({ id: 'changeRequest.commentField.notes', message: 'Notes' });
    case 'special_care':
      return i18n._({ id: 'changeRequest.commentField.special_care', message: 'Special care' });
    case 'note_emergency_list':
      return i18n._({ id: 'changeRequest.commentField.note_emergency_list', message: 'Emergency list note' });
    case 'language_request':
      return i18n._({ id: 'changeRequest.commentField.language_request', message: 'Language request' });
    case 'sleep_request':
      return i18n._({ id: 'changeRequest.commentField.sleep_request', message: 'Sleep request' });
    case 'eating_request':
      return i18n._({ id: 'changeRequest.commentField.eating_request', message: 'Eating request' });
    case 'toilet_request':
      return i18n._({ id: 'changeRequest.commentField.toilet_request', message: 'Toilet request' });
    case 'misc_request':
      return i18n._({ id: 'changeRequest.commentField.misc_request', message: 'Miscellaneous request' });
    case 'vaccination_comment':
      return i18n._({ id: 'changeRequest.commentField.vaccination_comment', message: 'Vaccination comment' });
    default:
      return field;
  }
}

// ---- typed payload view (read side) ----------------------------------------

export interface ChangeRequestExecutorResultView {
  status?: string;
  status_code?: number;
  detail?: string;
  executed_at?: string;
  /** A follow-up workflow item the apply opened (the termination case). */
  reference_category?: string;
  reference_id?: string;
}

export interface ChangeRequestCandidateView {
  child_id?: string;
  contract_group_id?: string;
  label?: string;
  confidence?: number;
}

export interface ChangeRequestReviewView {
  edited?: boolean;
  edited_by?: string;
  edited_at?: string;
}

export interface ChangeRequestPayloadView {
  family?: string;
  sub_type?: string;
  source?: { kind?: string; message_id?: string; raw_excerpt?: string };
  resolved?: {
    child_id?: string;
    contract_group_id?: string;
    version_id?: string;
    confidence?: number;
    candidates?: ChangeRequestCandidateView[];
  };
  executor?: {
    endpoint?: string;
    body?: Record<string, unknown>;
    result?: ChangeRequestExecutorResultView;
  };
  review?: ChangeRequestReviewView;
  /**
   * Server-forced provenance of a parent-app request (#1715). Only these two
   * string keys are read; anything else under `extracted` is ignored.
   */
  extracted?: ChangeRequestExtractedView;
}

export interface ChangeRequestExtractedView {
  /** Contact id of the parent who sent the request. */
  requested_by_contact_id?: string;
  /** Clerk user id of the admin who sent it while impersonating the parent. */
  impersonated_by_user_id?: string;
}

// Copy only the two known string keys of `payload.extracted` (#1715).
function parseExtracted(raw: unknown): ChangeRequestExtractedView | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const out: ChangeRequestExtractedView = {};
  if (typeof r.requested_by_contact_id === 'string' && r.requested_by_contact_id !== '') {
    out.requested_by_contact_id = r.requested_by_contact_id;
  }
  if (typeof r.impersonated_by_user_id === 'string' && r.impersonated_by_user_id !== '') {
    out.impersonated_by_user_id = r.impersonated_by_user_id;
  }
  return out.requested_by_contact_id || out.impersonated_by_user_id ? out : undefined;
}

/**
 * Best-effort parse of a workflow item's `payload` (which arrives as an opaque
 * object) into the typed change_request view. Returns null when the shape is not
 * a change_request payload.
 */
export function parseChangeRequestPayload(payload: unknown): ChangeRequestPayloadView | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const p = payload as ChangeRequestPayloadView;
  if (!p.family && !p.sub_type) {
    return null;
  }
  if (p.extracted === undefined) {
    return p;
  }
  // Narrow `extracted` to the two known string keys (never pass the raw object on).
  return { ...p, extracted: parseExtracted(p.extracted) };
}

/**
 * Names of the children and the requesting parent of a change_request item, sent
 * by the workflow-items list/detail/transition responses as
 * `change_request_subject`. Aliases of the generated DTOs: every field is
 * optional, so readers must guard missing ids and names.
 */
export type ChangeRequestSubjectChild = workflows_ChangeRequestSubjectChildDTO;
export type ChangeRequestSubject = workflows_ChangeRequestSubjectDTO;

/**
 * At most this many child cards head a change request. A bulk request above the
 * cap keeps its "Target children" summary row instead.
 */
export const MAX_CHANGE_REQUEST_CHILD_CARDS = 5;

/**
 * The children a change request is about, in payload order without duplicates:
 * `resolved.child_id` (parent contract / group_allocation), then
 * `executor.body.child_id` and `executor.body.child_ids` (children family).
 */
export function changeRequestChildIds(parsed: ChangeRequestPayloadView | null): string[] {
  if (!parsed) return [];
  const body = parsed.executor?.body;
  const candidates: unknown[] = [
    parsed.resolved?.child_id,
    body?.child_id,
    ...(Array.isArray(body?.child_ids) ? body.child_ids : []),
  ];
  const ids: string[] = [];
  for (const id of candidates) {
    if (typeof id === 'string' && id !== '' && !ids.includes(id)) ids.push(id);
  }
  return ids;
}

// ---- triage grouping -------------------------------------------------------

// Confidence at or above this threshold (or absent — high-trust web_admin/app
// producers don't emit a confidence) is treated as "clean enough to press
// through" (Routine). Below it (and > 0) the item needs your review.
export const TRIAGE_CONFIDENCE_THRESHOLD = 0.8;

// Terminal change_request states — the change is done (or dispositioned) and the
// item is no longer part of the live triage worklist. Mirrors the backend
// `change_request` FSM terminal set {executed, failed, rejected, resolved,
// cancelled}; cancelled is a request the parent withdrew.
export const CHANGE_REQUEST_TERMINAL_STATES: ReadonlySet<string> = new Set([
  'executed',
  'failed',
  'rejected',
  'resolved',
  'cancelled',
]);

/**
 * A change_request whose work is finished — excluded from the active triage
 * buckets so the board/list stay a live worklist, not a log.
 */
export function isChangeRequestTerminalState(state: string | undefined | null): boolean {
  return CHANGE_REQUEST_TERMINAL_STATES.has(state ?? '');
}

export type TriageBucket = 'needs_handholding' | 'press_through';

// Contract kinds that only shift billable care inside the running contract. Every
// other contract kind (terminate, start_end, other, or a missing/unknown kind)
// needs a person.
const ROUTINE_CONTRACT_KINDS: ReadonlySet<string> = new Set(['care_days', 'pause']);

/**
 * True for a change request that a person must review one by one, whatever its
 * source or confidence: it changes who may take the child (`child_contact`, or
 * a `children` `contact_pickup` / `contact_emergency` grant),
 * where the child is (`group_allocation`), or ends or reshapes the contract (any
 * `contract` kind except `care_days` and `pause`), or asks for the family's data
 * or the deletion of the account (a `contact_data` privacy request, #1876). Such
 * an item is always "Needs your review" and never joins a batch approve. A missing payload counts as
 * needing a person.
 */
export function requiresPersonalReview(view: ChangeRequestPayloadView | null | undefined): boolean {
  if (!view) return true;
  switch (view.family) {
    case 'child_contact':
    case 'group_allocation':
      return true;
    // Granting an existing contact pick-up or emergency rights changes who may take the child.
    case 'children':
      return view.sub_type === 'contact_pickup' || view.sub_type === 'contact_emergency';
    // A data access or account deletion request (#1876) is handled by a person
    // outside the app, so it never joins a batch approve.
    case 'contact_data':
      return view.sub_type === 'data_access_request' || view.sub_type === 'account_deletion_request';
    case 'contract': {
      // The kind lives in sub_type and in executor.body.kind; both must be routine.
      const bodyKind = view.executor?.body?.kind;
      const kinds = [view.sub_type, typeof bodyKind === 'string' ? bodyKind : undefined].filter(
        (k): k is string => typeof k === 'string' && k !== '',
      );
      return kinds.length === 0 || kinds.some((k) => !ROUTINE_CONTRACT_KINDS.has(k));
    }
    default:
      return false;
  }
}

/**
 * True when a workflow item may join a batch approve ("Approve all", "Approve
 * selected"): a pending change_request whose FSM still offers `approve`, in the
 * press-through bucket, and not a kind that needs a person. The kind check repeats
 * the classifier on purpose, so a sensitive kind never batch-approves even if it
 * lands in a batchable group.
 */
export function isBatchApprovableChangeRequest(item: {
  category?: string;
  current_state?: string;
  available_actions?: string[];
  payload?: unknown;
}): boolean {
  if (item.category !== 'change_request' || item.current_state !== 'pending') return false;
  if (!(item.available_actions ?? []).includes('approve')) return false;
  const view = parseChangeRequestPayload(item.payload);
  if (requiresPersonalReview(view)) return false;
  return classifyChangeRequestTriage(view, item.current_state) === 'press_through';
}

/**
 * Classify a change_request into a triage bucket from its payload + current
 * state. "Needs your review" = manual state, a kind that needs a person
 * ({@link requiresPersonalReview}: termination, new contact, group move), email
 * source, multiple resolution candidates (ambiguous target), or a present-but-low
 * confidence. Everything else (clean high-confidence / web_admin / app, single
 * candidate, undefined confidence) is "Routine".
 */
export function classifyChangeRequestTriage(
  view: ChangeRequestPayloadView | null | undefined,
  state: string,
): TriageBucket {
  if (state === 'manual') return 'needs_handholding';
  // A missing/unparseable payload must never fast-track: route it to manual
  // review rather than treating a malformed item as safe-to-bulk-approve.
  if (!view) return 'needs_handholding';
  if (requiresPersonalReview(view)) return 'needs_handholding';
  if (view.source?.kind === 'email') return 'needs_handholding';
  if ((view.resolved?.candidates?.length ?? 0) > 1) return 'needs_handholding';
  const c = view.resolved?.confidence;
  if (typeof c === 'number' && c > 0 && c < TRIAGE_CONFIDENCE_THRESHOLD) return 'needs_handholding';
  return 'press_through';
}

export function getTriageBucketLabel(i18n: I18nLike, bucket: TriageBucket): string {
  return bucket === 'needs_handholding'
    ? i18n._({ id: 'workflow.triage.needsReview', message: 'Needs your review' })
    : i18n._({ id: 'workflow.triage.routine', message: 'Routine' });
}

/**
 * Why a live change request cannot join "Approve all", in one sentence for the
 * lock icon on its row, or `null` when it can (or when batching does not apply:
 * another category, a finished request, no approve on offer). Mirrors the
 * checks of {@link classifyChangeRequestTriage} in the same order.
 */
export function getBatchApproveBlockReason(
  i18n: I18nLike,
  item: { category?: string; current_state?: string; available_actions?: string[]; payload?: unknown },
): string | null {
  if (item.category !== 'change_request') return null;
  if (item.current_state === 'manual') {
    return i18n._({
      id: 'workflow.triage.lock.manual',
      message: 'Could not be processed automatically. Open it to decide.',
    });
  }
  if (item.current_state !== 'pending') return null;
  if (!(item.available_actions ?? []).includes('approve')) return null;
  const view = parseChangeRequestPayload(item.payload);
  if (!view) {
    return i18n._({
      id: 'workflow.triage.lock.unreadable',
      message: 'The request details could not be read. Open it to check.',
    });
  }
  if (requiresPersonalReview(view)) {
    return i18n._({
      id: 'workflow.triage.lock.personalReview',
      message: 'Changes who may take the child, where the child is, or the contract. Review it on its own.',
    });
  }
  if (view.source?.kind === 'email') {
    return i18n._({
      id: 'workflow.triage.lock.email',
      message: 'Came in by email. Check that it was matched to the right child.',
    });
  }
  if ((view.resolved?.candidates?.length ?? 0) > 1) {
    return i18n._({
      id: 'workflow.triage.lock.ambiguous',
      message: 'Matches more than one child. Choose the right one first.',
    });
  }
  const c = view.resolved?.confidence;
  if (typeof c === 'number' && c > 0 && c < TRIAGE_CONFIDENCE_THRESHOLD) {
    return i18n._({
      id: 'workflow.triage.lock.lowConfidence',
      message: 'Matched with low confidence. Check it before approving.',
    });
  }
  return null;
}

export function getSourceLabel(i18n: I18nLike, kind: string | undefined): string {
  switch (kind) {
    case 'web_admin':
      return i18n._({ id: 'workflow.triage.source.webAdmin', message: 'Web admin' });
    case 'email':
      return i18n._({ id: 'workflow.triage.source.email', message: 'Email' });
    case 'app':
      return i18n._({ id: 'workflow.triage.source.app', message: 'App' });
    default:
      return kind ?? '';
  }
}

/**
 * Format a 0..1 confidence as a percentage string (e.g. 0.92 → "92%"), or "—"
 * when absent/non-positive (high-trust producers omit it).
 */
export function formatConfidence(confidence: number | undefined): string {
  if (typeof confidence !== 'number' || confidence <= 0) return '—';
  return `${Math.round(confidence * 100)}%`;
}

/**
 * Build the PATCH body (ChangeRequestInputDTO shape) for an in-place review edit
 * from the canonical children spec. family is fixed to 'children'; resolved links
 * the single target when unambiguous.
 */
export function buildChildDataUpdateEditBody(params: {
  subType: string;
  childIds: string[];
  comment?: ChildDataUpdateSpec['comment'];
  allergy?: ChildDataUpdateSpec['allergy'];
  medical?: ChildDataUpdateSpec['medical'];
  contact?: ChildDataUpdateSpec['contact'];
}): {
  family: string;
  sub_type: string;
  resolved: { child_id: string };
  executor: { body: ChildDataUpdateSpec };
} {
  const body: ChildDataUpdateSpec = { child_ids: params.childIds };
  switch (params.subType) {
    case 'comment':
      if (params.comment) body.comment = params.comment;
      break;
    case 'allergy':
      if (params.allergy) body.allergy = params.allergy;
      break;
    case 'medical':
      if (params.medical) body.medical = params.medical;
      break;
    case 'contact_pickup':
    case 'contact_emergency':
      if (params.contact) body.contact = params.contact;
      break;
    default:
      break;
  }
  return {
    family: 'children',
    sub_type: params.subType,
    resolved: { child_id: params.childIds.length === 1 ? params.childIds[0] : '' },
    executor: { body },
  };
}

// ---- producer-agnostic create API (write side) -----------------------------

/**
 * Canonical `children` family executor body — the shape the backend
 * `child_data_update` adapter decodes from `payload.executor.body`. Carries
 * `child_ids` plus exactly one of `comment` / `allergy` / `medical` / `contact`,
 * selected by `payload.sub_type`. NOTE the shape difference from the legacy
 * standalone DTO: `comment: { field, value }`, not `comment_field`/`comment_value`.
 */
export interface ChildDataUpdateSpec {
  child_ids: string[];
  comment?: { field: string; value: string };
  allergy?: { operation: string; allergy_id?: string; name?: string; severity?: string; comment?: string };
  medical?: { kind: string; operation: string; id?: string; name?: string; type?: string; dosage?: string; comment?: string };
  contact?: { contact_id: string; grant: boolean };
  /** sub_type `basic` (parent app): master-data correction; an absent field stays as it is. */
  basic?: { firstname?: string; lastname?: string; birthdate?: string; gender?: string; nationalities?: string[] };
}

export type ChangeRequestSourceKind = 'web_admin' | 'email' | 'app';

/**
 * Flat form-values shape shared by the canonical create form and the prefill
 * mapper. Mapped to the nested `ChildDataUpdateSpec` on submit.
 */
export interface ChangeRequestFormValues extends Record<string, unknown> {
  childIds: string[];
  subType: string;
  commentField: string;
  commentValue: string;
  allergyOperation: string;
  allergyId: string;
  allergyName: string;
  allergySeverity: string;
  allergyComment: string;
  medicalKind: string;
  medicalOperation: string;
  medicalId: string;
  medicalName: string;
  medicalType: string;
  medicalDosage: string;
  medicalComment: string;
  contactId: string;
  contactGrant: boolean;
}

export interface BuildCreateChangeRequestParams {
  subType: string;
  childIds: string[];
  comment?: ChildDataUpdateSpec['comment'];
  allergy?: ChildDataUpdateSpec['allergy'];
  medical?: ChildDataUpdateSpec['medical'];
  contact?: ChildDataUpdateSpec['contact'];
  source?: { kind?: ChangeRequestSourceKind; message_id?: string };
}

/**
 * Build a producer-agnostic create batch for the `children` family. One
 * submission = one request in the batch. The full target set (single or bulk)
 * lives in `executor.body.child_ids`; `resolved.child_id` links the item's
 * `reference_id` only when there is exactly one target. The only producer-specific
 * value is `source.kind` (web_admin here) — the request shape is identical to what
 * the email / app channels post.
 */
export function buildCreateChangeRequestBatch(
  params: BuildCreateChangeRequestParams,
): change_requests_CreateChangeRequestsRequest {
  const { subType, childIds } = params;

  const body: ChildDataUpdateSpec = { child_ids: childIds };
  switch (subType) {
    case 'comment':
      if (params.comment) body.comment = params.comment;
      break;
    case 'allergy':
      if (params.allergy) body.allergy = params.allergy;
      break;
    case 'medical':
      if (params.medical) body.medical = params.medical;
      break;
    case 'contact_pickup':
    case 'contact_emergency':
      if (params.contact) body.contact = params.contact;
      break;
    default:
      break;
  }

  return {
    source: {
      kind: (params.source?.kind ?? 'web_admin') as change_requests_CreateChangeRequestsRequest['source']['kind'],
      message_id: params.source?.message_id ?? crypto.randomUUID(),
    },
    requests: [
      {
        family: 'children',
        sub_type: subType,
        resolved: { child_id: childIds.length === 1 ? childIds[0] : '' },
        executor: { body },
      },
    ],
  };
}

// ---- care_day / pause producers (write side) -------------------------------

/**
 * Canonical `care_day` family executor body (#783) — mirrors the Go
 * `CareDayChangeSpec` (care_day_executor.go) field-for-field. `weekday` is an ISO
 * weekday 1..7; `active_from`/`active_to` are bare `YYYY-MM-DD` dates. The op
 * (add/remove) is derived server-side from `sub_type` (`add_day`/`remove_day`).
 */
export interface CareDayChangeSpec {
  contract_group_id: string;
  version_id?: string;
  weekday: number;
  active_from: string;
  active_to: string;
  reason?: string;
}

/**
 * Canonical `pause` family executor body (#784) — mirrors the Go `PauseChangeSpec`
 * (contract_pause_executor.go). `pause_from`/`pause_to` are bare `YYYY-MM-DD`
 * dates; the op (create/extend/cancel) is derived server-side from `sub_type`.
 */
export interface PauseChangeSpec {
  contract_group_id: string;
  version_id?: string;
  pause_from: string;
  pause_to: string;
  reason?: string;
}

/**
 * Flat RHF form-values shape for the care_day create form. NOTE the runtime
 * representations imposed by BaseEditableForm's field renderers (mirrors the
 * RateVersionForm precedent): a `type:'date'` field emits a `Date` object and a
 * `type:'select'` field emits a `string`, so the date fields hold `Date` and
 * `weekday` holds a string here. They are serialized to the spec's
 * `YYYY-MM-DD` string / numeric `weekday` at the API boundary in the form's submit.
 */
export interface CareDayRequestFormValues extends Record<string, unknown> {
  subType: string;
  weekday: string;
  activeFrom?: Date;
  activeTo?: Date;
  reason: string;
}

/**
 * Flat RHF form-values shape for the pause create form. Same runtime-representation
 * note as {@link CareDayRequestFormValues}: date fields hold `Date`, serialized to
 * `YYYY-MM-DD` on submit.
 */
export interface PauseRequestFormValues extends Record<string, unknown> {
  subType: string;
  pauseFrom?: Date;
  pauseTo?: Date;
  reason: string;
}

export interface BuildCareDayBatchParams {
  subType: string;
  contractGroupId: string;
  versionId?: string;
  weekday: number;
  activeFrom: string;
  activeTo: string;
  reason?: string;
  source?: { kind?: ChangeRequestSourceKind; message_id?: string };
}

/**
 * Build a producer-agnostic create batch for the `care_day` family. Produces the
 * exact `executor.body` = {@link CareDayChangeSpec} the merged care_day executor
 * consumes, and mirrors the children builder's `source` defaulting (web_admin +
 * a random `message_id`). `contract_group_id` is set in BOTH `resolved` and
 * `executor.body` so `isInScopeCareDay` scopes the item and the triage read-side
 * (`resolved.contract_group_id`) is populated. `version_id`/`reason` are omitted
 * (not sent as empty strings) when absent.
 */
export function buildCareDayBatch(
  params: BuildCareDayBatchParams,
): change_requests_CreateChangeRequestsRequest {
  const versionId = params.versionId || undefined;
  const reason = params.reason?.trim() || undefined;
  const body: CareDayChangeSpec = {
    contract_group_id: params.contractGroupId,
    version_id: versionId,
    weekday: params.weekday,
    active_from: params.activeFrom,
    active_to: params.activeTo,
    reason,
  };

  return {
    source: {
      kind: (params.source?.kind ?? 'web_admin') as change_requests_CreateChangeRequestsRequest['source']['kind'],
      message_id: params.source?.message_id ?? crypto.randomUUID(),
    },
    requests: [
      {
        family: 'care_day',
        sub_type: params.subType,
        resolved: { contract_group_id: params.contractGroupId, version_id: versionId },
        executor: { body },
      },
    ],
  };
}

export interface BuildPauseBatchParams {
  subType: string;
  contractGroupId: string;
  versionId?: string;
  pauseFrom: string;
  pauseTo: string;
  reason?: string;
  source?: { kind?: ChangeRequestSourceKind; message_id?: string };
}

/**
 * Build a producer-agnostic create batch for the `pause` family. Produces the
 * exact `executor.body` = {@link PauseChangeSpec} the merged pause executor
 * consumes. Same `source` defaulting + `resolved`/`executor.body` dual-write as
 * {@link buildCareDayBatch}.
 */
export function buildPauseBatch(
  params: BuildPauseBatchParams,
): change_requests_CreateChangeRequestsRequest {
  const versionId = params.versionId || undefined;
  const reason = params.reason?.trim() || undefined;
  const body: PauseChangeSpec = {
    contract_group_id: params.contractGroupId,
    version_id: versionId,
    pause_from: params.pauseFrom,
    pause_to: params.pauseTo,
    reason,
  };

  return {
    source: {
      kind: (params.source?.kind ?? 'web_admin') as change_requests_CreateChangeRequestsRequest['source']['kind'],
      message_id: params.source?.message_id ?? crypto.randomUUID(),
    },
    requests: [
      {
        family: 'pause',
        sub_type: params.subType,
        resolved: { contract_group_id: params.contractGroupId, version_id: versionId },
        executor: { body },
      },
    ],
  };
}

// Per-request routing classes returned by the create API (mirrors the backend
// `requires_approval` policy outcomes).
export const CHANGE_REQUEST_ROUTINGS = [
  'pending',
  'executed',
  'failed',
  'manual',
  'delegated_termination',
  'error',
] as const;

export type ChangeRequestRouting = (typeof CHANGE_REQUEST_ROUTINGS)[number];

export interface RoutingToast {
  kind: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

/**
 * Map a per-request `routing` class to a localized toast. The toast reflects the
 * real per-request outcome (auto-applied, sent for approval, failed, captured for
 * manual handling, or a create/route error), never a blanket success.
 */
export function getRoutingToast(
  i18n: I18nLike,
  result: change_requests_ChangeRequestResultDTO | undefined,
): RoutingToast {
  switch (result?.routing) {
    case 'executed':
      return {
        kind: 'success',
        message: i18n._({ id: 'changeRequest.toast.autoApplied', message: 'Change applied automatically' }),
      };
    case 'pending':
      return {
        kind: 'success',
        message: i18n._({ id: 'changeRequest.toast.sentForApproval', message: 'Change request sent for approval' }),
      };
    case 'failed':
      return {
        kind: 'error',
        message: i18n._({ id: 'changeRequest.toast.autoApplyFailed', message: 'The change could not be applied' }),
      };
    case 'manual':
      return {
        kind: 'info',
        message: i18n._({ id: 'changeRequest.toast.capturedManual', message: 'Captured for manual handling' }),
      };
    case 'delegated_termination':
      return {
        kind: 'warning',
        message: i18n._({
          id: 'changeRequest.toast.delegatedTermination',
          message: 'Change request delegated for termination handling',
        }),
      };
    case 'error':
      return {
        kind: 'error',
        message:
          result?.error ||
          i18n._({ id: 'changeRequest.toast.createFailed', message: 'Failed to create the change request' }),
      };
    default:
      return {
        kind: 'success',
        message: i18n._({ id: 'changeRequest.toast.created', message: 'Change request created' }),
      };
  }
}

/**
 * Map a parsed change_request payload view (e.g. an email-sourced item) into the
 * flat form-values shape, so the canonical form can render it pre-filled and
 * editable in review mode. Reads `view.sub_type` + `view.executor.body`
 * (a `ChildDataUpdateSpec`).
 */
export function changeRequestPayloadToFormValues(
  view: ChangeRequestPayloadView | null | undefined,
): Partial<ChangeRequestFormValues> {
  if (!view) return {};
  const body = (view.executor?.body ?? {}) as Partial<ChildDataUpdateSpec>;
  const values: Partial<ChangeRequestFormValues> = {
    subType: view.sub_type,
    childIds: Array.isArray(body.child_ids) ? body.child_ids : [],
  };

  if (body.comment) {
    values.commentField = body.comment.field || 'notes';
    values.commentValue = body.comment.value ?? '';
  }
  if (body.allergy) {
    values.allergyOperation = body.allergy.operation || 'add';
    values.allergyId = body.allergy.allergy_id ?? '';
    values.allergyName = body.allergy.name ?? '';
    values.allergySeverity = body.allergy.severity ?? '';
    values.allergyComment = body.allergy.comment ?? '';
  }
  if (body.medical) {
    values.medicalKind = body.medical.kind || 'disease';
    values.medicalOperation = body.medical.operation || 'add';
    values.medicalId = body.medical.id ?? '';
    values.medicalName = body.medical.name ?? '';
    values.medicalType = body.medical.type ?? '';
    values.medicalDosage = body.medical.dosage ?? '';
    values.medicalComment = body.medical.comment ?? '';
  }
  if (body.contact) {
    values.contactId = body.contact.contact_id ?? '';
    values.contactGrant = body.contact.grant ?? true;
  }

  return values;
}
